/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect } from "react";
import { compose } from "redux";
import moment from "moment";
import { withStyles } from "components/common";
import Table from "components/common/Table";
import { useMutation } from "@apollo/react-hooks";
import { CANCEL_SCHEDULED_PAYMENT } from "utils";

const PAYMENT_TYPES = {
  SCHEDULED: {
    type: "SCHEDULED",
    color: "#FFE9AB",
  },
  PROCESSED: {
    type: "PROCESSED",
    color: "#E6FFED",
  },
  CANCELED: {
    type: "CANCELED",
    color: "#DDDDDD",
  },
};

const computeColumns = (user) => [
  {
    Header: "Payment Created",
    accessor: (data) =>
      (data.created_at
        ? `${moment.unix(data.created_at / 1000).format("M/D/YYYY LT")}`
        : "unknown"),
  },
  {
    Header: "Scheduled Date",
    accessor: (data) =>
      (data.scheduled_date
        ? `${moment(data.scheduled_date, "YYYY-MM-DD").format("MM/DD/YYYY")}`
        : "unknown"),
  },
  {
    Header: "Payment Processed",
    accessor: (data) =>
      (data.processed_at
        ? `${moment.unix(data.processed_at / 1000).format("M/D/YYYY LT")}`
        : ""),
  },
  {
    Header: "Amount",
    accessor: (data) => `${data.amount}${data.revised ? " (revised)" : ""}`,
  },
  {
    Header: "Payment Method",
    accessor: (data) =>
      (data.payment_method ? (
        <div className="text-sm">
          <div>{data.payment_method.institution}</div>
          <div>{data.payment_method.nickname}</div>
          <div className="text-gray-700">
            ({data.payment_method.type}) -
            {data.payment_method.account_number_last_four}
          </div>
        </div>
      ) : (
        ""
      )),
  },
  {
    Header: "Status",
    accessor: (data) => <StatusComponent data={data} user={user} />,
  },
  {
    Header: "Created By",
    accessor: "created_by",
  },
];

const StatusComponent = ({ data, user }) => {
  let timer;

  const [statusText, setStatusText] = useState(data.status);

  const [cancelPayment] = useMutation(
    CANCEL_SCHEDULED_PAYMENT,
    {
      onCompleted: () => {
        setStatusText("CANCELED");
      },
      onError: () => {
        timer = setTimeout(() => {
          setStatusText(data.status);
        }, 5000);
        setStatusText("Error canceling");
      },
    },
  );

  useEffect(() => {
    return () => timer ?? clearTimeout(timer);
  }, []);

  const cancel = () => {
    // eslint-disable-next-line no-alert
    const shouldCancelPayment = window.confirm(
      "Are you sure you want to cancel this payment?",
    );
    if (shouldCancelPayment) {
      cancelPayment({
        variables: {
          customersEmail: user.email,
          scheduledPaymentUuid: data.uuid,
        },
      });
      setStatusText("canceling...");
    }
  };
  return (
    <section>
      <span>{statusText}</span>
      {statusText === PAYMENT_TYPES.SCHEDULED.type && (
        <button type="button" onClick={cancel}>
          &nbsp;(cancel)
        </button>
      )}
    </section>
  );
};

const TableSection = ({ filteredPayments, user, columns, getRowStyle }) =>
  (filteredPayments.length ? (
    <Table
      id="scheduled-payments"
      user={user}
      data={filteredPayments}
      columns={columns}
      getRowStyle={getRowStyle}
    />
  ) : (
    <div>Currently there are no scheduled payments.</div>
  ));

const UserScheduledPaymentsTable = ({
  user,
  payments = [],
  payment_methods: paymentMethods = [],
  loading,
}) => {
  const columns = useMemo(() => computeColumns(user), [user]);
  const pms = {};
  paymentMethods.forEach((pm) => {
    pms[pm.id] = pm;
  });
  payments.forEach((pp) => {
    pp.payment_method = pms[pp.payment_method_id];
  });
  const [filteredPayments] = useState(payments);
  filteredPayments.sort(
    (a, b) => parseInt(b.created_at, 10) - parseInt(a.created_at, 10),
  );
  const getRowStyle = (row) => ({
    backgroundColor: PAYMENT_TYPES[row.original.status]?.color || "#FFF",
  });
  return (
    <div className="mt-5">
      <h1 className="text-2xl font-title mb-2 font-light">
        Scheduled Payments
      </h1>
      <div className="my-6">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <TableSection
            columns={columns}
            filteredPayments={filteredPayments}
            getRowStyle={getRowStyle}
            user={user}
          />
        )}
      </div>
    </div>
  );
};

export default compose(withStyles)(UserScheduledPaymentsTable);
